import React from "react";
import { useParams, Navigate } from "react-router-dom";
import { Check, Film, ShoppingBag, Megaphone, Clock, Layers, Zap } from "lucide-react";
import { Section, Button, Card, Badge } from "@vild/site-ui";

// --- AGENCIES ---
const AgenciesPage = () => (
  <div className="bg-zinc-950 min-h-screen">
    <Section className="pt-32 pb-16">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <Badge className="bg-indigo-500/10 text-indigo-400 border-indigo-500/20 mb-6">For Agencies</Badge>
          <h1 className="text-5xl font-bold mb-6 text-white">Scale deliverables without scaling headcount.</h1>
          <p className="text-xl text-zinc-400 mb-8">
            Stop manually exporting 20 versions for every client. Automate your post-production workflow and increase margins.
          </p>
          <ul className="space-y-4 mb-8">
            <li className="flex items-center gap-3 text-zinc-300">
              <Check className="text-indigo-500" /> Multi-tenant workspaces
            </li>
            <li className="flex items-center gap-3 text-zinc-300">
              <Check className="text-indigo-500" /> Precise client billing
            </li>
            <li className="flex items-center gap-3 text-zinc-300">
              <Check className="text-indigo-500" /> Standardized quality control
            </li>
          </ul>
          <Button size="lg" asLink="/signup?starter=social-clips">
            Try Social Clips Pipeline
          </Button>
        </div>
        <div className="relative">
          <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6 shadow-2xl rotate-2 hover:rotate-0 transition-transform duration-500">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-bold text-white">Client: Nike (Q3 Campaign)</h3>
              <span className="text-xs text-zinc-500">Processing...</span>
            </div>
            <div className="space-y-3">
              <div className="bg-zinc-800 p-3 rounded border border-zinc-700 flex justify-between items-center">
                <span className="text-sm text-zinc-200">IG Story (9:16)</span>
                <Badge className="bg-green-500/20 text-green-400 border-transparent">Done</Badge>
              </div>
              <div className="bg-zinc-800 p-3 rounded border border-zinc-700 flex justify-between items-center">
                <span className="text-sm text-zinc-200">YT Short (9:16)</span>
                <Badge className="bg-green-500/20 text-green-400 border-transparent">Done</Badge>
              </div>
              <div className="bg-zinc-800 p-3 rounded border border-zinc-700 flex justify-between items-center">
                <span className="text-sm text-zinc-200">LinkedIn (4:5)</span>
                <Badge className="bg-yellow-500/20 text-yellow-400 border-transparent">Encoding</Badge>
              </div>
              <div className="bg-zinc-800 p-3 rounded border border-zinc-700 flex justify-between items-center">
                <span className="text-sm text-zinc-200">Twitter (16:9)</span>
                <Badge className="bg-zinc-700 text-zinc-400 border-transparent">Queued</Badge>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
    <div className="bg-zinc-900 py-16">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <h2 className="text-2xl font-bold mb-8">Trusted by creative ops teams at</h2>
        <div className="flex flex-wrap justify-center gap-12 grayscale opacity-50">
          <div className="text-xl font-bold font-mono">MEDIA.MONKS</div>
          <div className="text-xl font-bold font-mono">DEPT</div>
          <div className="text-xl font-bold font-mono">OGILVY</div>
        </div>
      </div>
    </div>
  </div>
);

// --- STUDIOS ---
const StudiosPage = () => (
  <div className="bg-black min-h-screen text-zinc-100">
    <div className="max-w-7xl mx-auto px-6 pt-32 pb-20">
      <div className="text-center mb-16">
        <Film className="w-12 h-12 mx-auto text-red-600 mb-6" />
        <h1 className="text-5xl md:text-7xl font-bold mb-8 tracking-tighter">
          Automate the <span className="text-red-600">cutting room floor</span>.
        </h1>
        <p className="text-2xl text-zinc-500 max-w-3xl mx-auto font-light">
          Your editors are too expensive to be doing file management. Offload transcoding, proxy generation, and watermarking to Vild.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <Clock className="w-8 h-8 text-zinc-400 mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Overnight Dailies</h3>
          <p className="text-zinc-500">Auto-sync from set to cloud. Dailies are processed and ready for edit when you wake up.</p>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <Layers className="w-8 h-8 text-zinc-400 mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Proxy Generation</h3>
          <p className="text-zinc-500">Automatically create ProRes proxies for every raw file ingested into your S3 bucket.</p>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <Zap className="w-8 h-8 text-zinc-400 mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Burn-ins & Watermarks</h3>
          <p className="text-zinc-500">Apply dynamic timecode and security watermarks to every review copy instantly.</p>
        </Card>
      </div>

      <div className="p-8 border border-zinc-800 rounded-2xl bg-gradient-to-br from-zinc-900 to-black text-center">
        <h2 className="text-2xl font-bold mb-4">Start with the Auto-Caption pipeline</h2>
        <p className="text-zinc-500 mb-8">Perfect for social deliverables. Transcribe, burn subtitles, and export.</p>
        <Button size="lg" asLink="/signup?starter=auto-caption">
          Deploy Now
        </Button>
      </div>
    </div>
  </div>
);

// --- MARKETING ---
const MarketingPage = () => (
  <div className="bg-zinc-950 min-h-screen">
    <Section className="pt-32 pb-20">
      <div className="max-w-4xl">
        <div className="text-pink-500 font-bold mb-4 flex items-center gap-2">
          <Megaphone className="w-5 h-5" /> MARKETING TEAMS
        </div>
        <h1 className="text-6xl font-bold mb-8 text-white">
          Turn one video into <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500">
            20 social assets.
          </span>
        </h1>
        <p className="text-xl text-zinc-400 mb-8 max-w-2xl">
          Don&apos;t let video production bottleneck your content calendar. Vild automatically extracts highlights, resizes for TikTok/Reels, and generates captions.
        </p>
        <Button size="lg" asLink="/signup?starter=social-clips">
          Start Repurposing
        </Button>
      </div>
    </Section>

    <Section className="border-t border-zinc-900 bg-zinc-900/20">
      <h2 className="text-3xl font-bold mb-12 text-center">Content Velocity Engine</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="aspect-[9/16] bg-zinc-800 rounded-lg border border-zinc-700 relative overflow-hidden group">
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-zinc-600 font-bold text-4xl opacity-20">#{i}</span>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
              <div className="h-2 w-12 bg-white/20 rounded mb-2"></div>
              <div className="h-2 w-20 bg-white/20 rounded"></div>
            </div>
            <div className="absolute top-2 right-2 px-2 py-1 bg-green-500 text-black text-[10px] font-bold rounded opacity-0 group-hover:opacity-100 transition-opacity">
              GENERATED
            </div>
          </div>
        ))}
      </div>
    </Section>
  </div>
);

// --- ECOMMERCE ---
const EcommercePage = () => (
  <div className="bg-zinc-50 dark:bg-zinc-950 min-h-screen">
    <Section className="pt-32 pb-20 text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 text-orange-600 mb-8">
        <ShoppingBag className="w-8 h-8" />
      </div>
      <h1 className="text-5xl font-bold mb-6 text-zinc-900 dark:text-white">Batch process product photos at scale.</h1>
      <p className="text-xl text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto mb-10">
        1,000 SKUs. 10 marketplaces. One click. Resize, background remove, and optimize your entire catalog in minutes.
      </p>
      <Button size="lg" asLink="/signup?starter=batch-resize">
        Optimize Your Catalog
      </Button>
    </Section>

    <Section>
      <div className="bg-zinc-100 dark:bg-zinc-900 rounded-xl p-8 border border-zinc-200 dark:border-zinc-800">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-zinc-200 dark:divide-zinc-800">
          <div>
            <div className="text-3xl font-bold text-zinc-900 dark:text-white mb-2">10x</div>
            <div className="text-sm text-zinc-500">Faster than manual</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-zinc-900 dark:text-white mb-2">100%</div>
            <div className="text-sm text-zinc-500">Consistent Specs</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-zinc-900 dark:text-white mb-2">$0.002</div>
            <div className="text-sm text-zinc-500">Cost per image</div>
          </div>
          <div className="border-none">
            <div className="text-3xl font-bold text-zinc-900 dark:text-white mb-2">24/7</div>
            <div className="text-sm text-zinc-500">Always on</div>
          </div>
        </div>
      </div>
    </Section>
  </div>
);

export const ICPTemplate: React.FC = () => {
  const { slug } = useParams();

  switch (slug) {
    case "agencies":
      return <AgenciesPage />;
    case "studios":
      return <StudiosPage />;
    case "marketing":
      return <MarketingPage />;
    case "ecommerce":
      return <EcommercePage />;
    default:
      return <Navigate to="/" replace />;
  }
};

